/**
 * SpeechService — Professionelle deutsche Sprachausgabe fuer Lenny.
 *
 * xAI TTS via Backend-Proxy → ElevenLabs → Web Speech API (Browser)
 *
 * Nur Begruessung + Assistenten-Antworten werden gesprochen.
 * Kurze Aktions-Meldungen (Objekt hinzugefuegt etc.) sind stumm.
 */

const DEFAULT_XAI_VOICE = 'rex'; // Maennlich, selbstbewusst & klar — passt zu Lenny
const DEFAULT_ELEVENLABS_VOICE = 'EXAVITQu4vr4xnSDxMaL'; // Sarah
const DEAD_KEYS = [
  'sk_87e0d8dc06ff66ff3b48bfd5e9d5e18cbe017969d2974a66',
  'sk_5b7f0c1002cd57fc3ef4e1f5d57abfc32269b05acac2fbc7',
];

class SpeechService {
  constructor() {
    this.enabled = false;
    this.volume = 0.9;
    this.voice = null;
    this._audioCache = {};
    this._currentAudio = null;
    this._userInteracted = false;
    this._speakGeneration = 0; // Verhindert doppelte Wiedergabe

    // ── Provider-Config wird vom Backend geladen ──
    this.provider = 'browser'; // Default bis Backend antwortet
    this.xaiVoice = DEFAULT_XAI_VOICE;
    this.useXai = false;
    this.useElevenLabs = false;
    this.elevenLabsKey = '';
    this.elevenLabsVoiceId = DEFAULT_ELEVENLABS_VOICE;

    // Config vom Backend laden — Promise fuer Warten in welcome()
    this._configReady = this._loadProviderConfig();

    // User interaction tracking
    const markInteracted = () => { this._userInteracted = true; };
    ['click', 'touchstart', 'keydown'].forEach(ev =>
      document.addEventListener(ev, markInteracted, { once: true, passive: true })
    );

    // Web Speech API fallback voice
    if ('speechSynthesis' in window) {
      const loadVoice = () => {
        const voices = speechSynthesis.getVoices();
        this.voice =
          voices.find(v => v.lang === 'de-DE' && v.name.includes('Anna')) ||
          voices.find(v => v.lang === 'de-DE') ||
          voices.find(v => v.lang.startsWith('de')) ||
          null;
      };
      speechSynthesis.onvoiceschanged = loadVoice;
      loadVoice();
    }

    // Speech-Recovery (2026-05-22): Wenn user 'selenny-speech=true' im localStorage hat,
    // aber NICHT das Flag 'selenny-speech-user-set' gesetzt hat, dann war das ein Default
    // aus einer alten Version → auf 'false' zurücksetzen, damit der Nutzer nicht ungewollt
    // beschallt wird.
    const userSet = localStorage.getItem('selenny-speech-user-set');
    const pref = localStorage.getItem('selenny-speech');
    if (userSet === 'true' && pref !== null) {
      this.enabled = pref === 'true';
    } else {
      // Wert aus Vor-2026-05-22 ignorieren und sicherheitshalber auf false setzen
      if (pref === 'true') {
        try { localStorage.setItem('selenny-speech', 'false'); } catch (e) {}
      }
      this.enabled = false;
    }
    // Preload wird nach Config-Load gestartet (in _loadProviderConfig)
  }

  // Provider-Config vom Backend laden
  async _loadProviderConfig() {
    try {
      const baseUrl = this._getDesignerBaseUrl();
      const resp = await Promise.race([
        fetch(`${baseUrl}/api/tts/settings`),
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 3000)),
      ]);
      if (resp.ok) {
        const cfg = await resp.json();
        this.provider = cfg.provider || 'browser';
        this.useXai = this.provider === 'xai';
        this.useElevenLabs = this.provider === 'elevenlabs';
        if (cfg.voice) {
          if (this.useXai) this.xaiVoice = cfg.voice;
          if (this.useElevenLabs) this.elevenLabsVoiceId = cfg.voice;
        }
        console.log('[Speech] Provider:', this.provider, '| Voice:', cfg.voice);
      }
    } catch {
      // Fallback: xAI trotzdem versuchen (Backend hat den Key)
      console.warn('[Speech] Config-Timeout, versuche xAI als Default');
      this.provider = 'xai';
      this.useXai = true;
    }
    this._preloadWelcome();
  }

  // Begruessung im Hintergrund vorladen (nur bei API-Provider)
  _preloadWelcome() {
    if (!this.enabled) return;
    if (this.provider === 'browser') return;
    this._preloadText('Willkommen im Selenny Designer! Ich bin Lenny, dein Assistent. Klick auf mich wenn du Hilfe brauchst.');
  }

  async _preloadText(rawText) {
    const text = this._fixPronunciation(rawText);
    const cacheKey = `xai:${this.xaiVoice}:${text}`;
    if (this._audioCache[cacheKey]) return;
    try {
      const baseUrl = this._getDesignerBaseUrl();
      const resp = await fetch(`${baseUrl}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: this.xaiVoice }),
      });
      if (resp.ok) {
        this._audioCache[cacheKey] = await resp.blob();
      }
    } catch {}
  }

  // Aussprache-Korrekturen fuer Fachbegriffe
  _fixPronunciation(text) {
    return text
      .replace(/Stanzen/gi, 'Schtanzen')
      .replace(/stanzen/gi, 'schtanzen')
      .replace(/Ösen/gi, 'Oesen')
      .replace(/Selenny/gi, 'Selenni');
  }

  // ── Settings ──

  setXaiVoice(voiceId) {
    this.xaiVoice = voiceId || DEFAULT_XAI_VOICE;
    localStorage.setItem('selenny-xai-tts-voice', voiceId || '');
  }

  getXaiVoices() {
    return [
      { id: 'ara', name: 'Ara', description: 'Warm & freundlich' },
      { id: 'eve', name: 'Eve', description: 'Energisch & froehlich' },
      { id: 'leo', name: 'Leo', description: 'Autoritaetiv & stark' },
      { id: 'rex', name: 'Rex', description: 'Selbstbewusst & klar' },
      { id: 'sal', name: 'Sal', description: 'Ruhig & ausgeglichen' },
    ];
  }

  setElevenLabsKey(key) {
    this.elevenLabsKey = key || '';
    this.useElevenLabs = !!this.elevenLabsKey;
    localStorage.setItem('selenny-elevenlabs-key', key || '');
  }

  setElevenLabsVoice(voiceId) {
    this.elevenLabsVoiceId = voiceId || DEFAULT_ELEVENLABS_VOICE;
    localStorage.setItem('selenny-elevenlabs-voice', voiceId || '');
  }

  toggle() {
    this.enabled = !this.enabled;
    localStorage.setItem('selenny-speech', String(this.enabled));
    // Aktive Nutzer-Entscheidung markieren — verhindert dass Recovery-Logik in
    // späteren Sessions die Einstellung zurücksetzt.
    try { localStorage.setItem('selenny-speech-user-set', 'true'); } catch (e) {}
    if (this.enabled) {
      this.speak('Sprachausgabe aktiviert');
    } else {
      this._stop();
    }
    return this.enabled;
  }

  _stop() {
    // Alle laufenden Wiedergaben + Requests ungueltig machen
    this._speakGeneration++;
    if (this._currentAudio) {
      this._currentAudio.pause();
      this._currentAudio = null;
    }
    if ('speechSynthesis' in window) speechSynthesis.cancel();
  }

  // ── Main speak method — immer ueber den konfigurierten Provider ──

  async speak(text) {
    console.log('[Speech] speak() called:', { text: text?.substring(0, 40), enabled: this.enabled, provider: this.provider, useXai: this.useXai });
    if (!this.enabled || !text) {
      console.log('[Speech] SKIP — enabled:', this.enabled, '| text:', !!text);
      return;
    }

    // Warten bis Config geladen ist
    try { await this._configReady; } catch {}
    console.log('[Speech] Config ready — provider:', this.provider, '| useXai:', this.useXai);

    text = this._fixPronunciation(text);
    this._stop();
    this._userInteracted = true;
    const gen = this._speakGeneration;

    // 1. xAI TTS
    if (this.useXai) {
      try {
        const ok = await this._speakXai(text, gen);
        if (ok) return;
      } catch (e) {
        console.warn('[Speech] xAI Fehler:', e.message);
      }
      if (gen !== this._speakGeneration) return;
    }

    // 2. ElevenLabs
    if (this.useElevenLabs) {
      try {
        const ok = await this._speakElevenLabs(text, gen);
        if (ok) return;
      } catch (e) {
        console.warn('[Speech] ElevenLabs Fehler:', e.message);
      }
      if (gen !== this._speakGeneration) return;
    }

    // 3. Browser fallback
    if (gen === this._speakGeneration) {
      this._speakWebAPI(text);
    }
  }

  // ── xAI TTS via Backend ──

  async _speakXai(text, gen) {
    const cacheKey = `xai:${this.xaiVoice}:${text}`;
    if (this._audioCache[cacheKey]) {
      if (gen !== this._speakGeneration) return false;
      return this._playBlob(this._audioCache[cacheKey], gen);
    }

    const baseUrl = this._getDesignerBaseUrl();
    const controller = new AbortController();
    // Timeout: 15s fuer lange Texte
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const resp = await fetch(`${baseUrl}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice: this.xaiVoice }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (gen !== this._speakGeneration) return false;

      if (!resp.ok) {
        console.warn('[Speech] TTS Backend HTTP', resp.status);
        return false;
      }

      const blob = await resp.blob();
      if (gen !== this._speakGeneration) return false;

      this._audioCache[cacheKey] = blob;
      return this._playBlob(blob, gen);
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name !== 'AbortError') {
        console.warn('[Speech] TTS error:', err.message);
      }
      return false;
    }
  }

  // ── ElevenLabs TTS ──

  async _speakElevenLabs(text, gen) {
    const cacheKey = `el:${this.elevenLabsVoiceId}:${text}`;
    if (this._audioCache[cacheKey]) {
      if (gen !== this._speakGeneration) return false;
      return this._playBlob(this._audioCache[cacheKey], gen);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const resp = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${this.elevenLabsVoiceId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'xi-api-key': this.elevenLabsKey,
          },
          body: JSON.stringify({
            text,
            model_id: 'eleven_multilingual_v2',
            voice_settings: { stability: 0.4, similarity_boost: 0.8, style: 0.35, use_speaker_boost: true },
          }),
          signal: controller.signal,
        }
      );
      clearTimeout(timeoutId);
      if (gen !== this._speakGeneration) return false;
      if (!resp.ok) return false;

      const blob = await resp.blob();
      if (gen !== this._speakGeneration) return false;
      this._audioCache[cacheKey] = blob;
      return this._playBlob(blob, gen);
    } catch {
      clearTimeout(timeoutId);
      return false;
    }
  }

  // ── Audio playback ──

  _playBlob(blob, gen) {
    return new Promise((resolve) => {
      // Veraltete Generation → nicht abspielen
      if (gen !== undefined && gen !== this._speakGeneration) {
        resolve(false);
        return;
      }

      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.volume = this.volume;
      this._currentAudio = audio;

      audio.onended = () => {
        URL.revokeObjectURL(url);
        this._currentAudio = null;
        resolve(true);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        this._currentAudio = null;
        resolve(false);
      };

      audio.play().catch(() => {
        URL.revokeObjectURL(url);
        this._currentAudio = null;
        resolve(false);
      });
    });
  }

  // ── Browser Web Speech API (fallback) ──

  _speakWebAPI(text) {
    if (!('speechSynthesis' in window)) return;
    speechSynthesis.cancel();
    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      utterance.volume = this.volume;
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      if (!this.voice) {
        const voices = speechSynthesis.getVoices();
        this.voice = voices.find(v => v.lang === 'de-DE') || voices.find(v => v.lang.startsWith('de')) || null;
      }
      if (this.voice) utterance.voice = this.voice;
      speechSynthesis.speak(utterance);
    }, 50);
  }

  _getDesignerBaseUrl() {
    const loc = window.location;
    const pathParts = loc.pathname.split('/');
    let idx = pathParts.indexOf('selenny-designer');
    if (idx < 0) idx = pathParts.indexOf('designer');
    if (idx >= 0) {
      // Each shop runs its own backend at <designer-path>/backend on the SAME origin.
      // Whitelabel isolation: never fall through to a foreign domain.
      return loc.origin + pathParts.slice(0, idx + 1).join('/') + '/backend';
    }
    return loc.origin;
  }

  // Mute/unmute
  mute() { this._muted = true; }
  unmute() { this._muted = false; }

  // ═══════════════════════════════════════════════════════════════
  // Kontextuelle Nachrichten
  // Nur Begruessung + Hilfe sprechen. Aktions-Meldungen sind stumm.
  // ═══════════════════════════════════════════════════════════════

  // ── STUMM — nur visuell, nicht gesprochen ──
  objectAdded() {}
  designSaved() {}
  addedToCart() {}
  dimensionsChanged() {}
  finishingChanged() {}
  bleedChanged() {}
  previewToggled() {}
  gridToggled() {}
  rulersToggled() {}
  objectDeleted() {}
  objectDuplicated() {}
  objectGrouped() {}
  objectUngrouped() {}
  undone() {}
  redone() {}
  imageAdded() {}
  qrAdded() {}
  textAdded() {}
  standardSizeApplied() {}

  // ── GESPROCHEN — Begruessung ──
  welcome() {
    this.speak('Willkommen im Selenny Designer! Ich bin Lenny, dein Assistent. Klick auf mich wenn du Hilfe brauchst.');
  }

  error(msg) { this.speak(`Fehler: ${msg}`); }
}

const speechService = new SpeechService();
export default speechService;
